import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ContractsService } from '../contracts.service';
import { DeliveryRateCard } from '../models/delivery-rate-card.model';
import { MenuItem } from 'primeng/api';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Location, CommonModule } from '@angular/common';
import {VehicleConfig} from "../../fleet/vehicle/params/vehicle-config";
import {SessionStorageService} from "../../services/session-storage.service";
import {VehicleCapacity} from "../../fleet/vehicle/params/vehicle-capacity";
import {VehicleType} from "../../fleet/vehicle/params/vehicle-type";
import {Contracts} from "../models/contracts.model";
import { Country } from "../../models/common/country.model";
import { City } from "../../models/common/city.model";
import { BaseMasterDataService } from "../../services/base-master-data.service";

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressBarModule } from "primeng/progressbar";
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';


@Component({
    selector: 'app-add-edit-rate-card',
    templateUrl: './add-edit-rate-card.component.html',
    styleUrls: ['./add-edit-rate-card.component.css'],
    standalone: true,
    imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    ToastModule,
    InputTextModule,
    ButtonModule,
    SelectModule,
    BreadcrumbModule,
    DatePickerModule,
    InputNumberModule,
    CheckboxModule,
    ToggleSwitchModule,
    ProgressBarModule,
    FieldsetModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent
],
    providers: [
        ContractsService,
        SessionStorageService,
        BaseMasterDataService
    ]
})
export class AddEditRateCardComponent implements OnInit {

    contract: Contracts;
    contractId: number | null;
    contractRevisionId: number | null;
    createEditRateCardForm!: FormGroup;
    createEditRateCard: DeliveryRateCard = new DeliveryRateCard();
    rateCardCreationInProgress = false;

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    vehicleTypes: VehicleType[] = [];
    vehicleConfigs: VehicleConfig[] = [];
    vehicleCapacities: VehicleCapacity[] = [];
    filteredVehicleConfigs: VehicleConfig[] = [];
    filteredVehicleCapacities: VehicleCapacity[] = [];

    countries: Country[] = [];
    sourceCities: City[] = [];
    destinationCities: City[] = [];
    isLoading: any;

    constructor(
        private fb: FormBuilder,
        private contractsService: ContractsService,
        private route: ActivatedRoute,
        private location: Location,
        private sessionStorageService: SessionStorageService,
        private baseMasterDataService: BaseMasterDataService
    ) { }

    ngOnInit(): void {
        this.countries = this.sessionStorageService.getCountryList() ?? this.defaultRateCountries();

        const vehicleTypes = this.sessionStorageService.getVehicleTypes();
        if (vehicleTypes) {
            this.vehicleTypes = vehicleTypes;
            this.vehicleConfigs = vehicleTypes.flatMap(value => value.vehicleConfigs);
            this.vehicleCapacities = vehicleTypes.flatMap(value => value.vehicleCapacities);
        }

        // Load existing rate card for editing
        const rateCardId = this.route.snapshot.paramMap.get('rateId');
        if (rateCardId) {
            this.loadRateCard(Number(rateCardId));
        }

        // Load existing rate card for editing
        this.contractId = Number(this.route.snapshot.paramMap.get('contractId') ?? 0);
        this.contractRevisionId = Number(this.route.snapshot.paramMap.get('contractRevisionId') ?? 0);

        this.contractsService.getContractById(this.contractId).subscribe(contract => {
            console.log(contract);
            this.contract = contract;
        });


        this.breadcrumbItems = [
            { label: 'Rate Cards', routerLink: '/contracts' }, // Adjust routerLink as needed
            { label: rateCardId ? 'Edit Rate Card' : 'Add Rate Card' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.createEditRateCardForm = this.fb.group({
            vehicleTypeDropdown: [''],
            vehicleConfigDropdown: [''],
            vehicleCapacityDropdown: [''],
            sourceCityInput: [''],
            sourceCountryInput: [''],
            destinationCityInput: [''],
            destinationCountryInput: [''],
            distanceInput: [''],
            amountInput: [''],
            goodsInsuranceInput: [''],
            portPermitInput: [''],
            waitingThresholdInput: [''],
            waitingChargeInput: [''],
            waitingLimitInput: ['']
        });

        this.syncCitiesForCurrentRate();
    }

    loadRateCard(rateCardId: number) {
        this.contractsService.getDeliveryRateCardByRateId(rateCardId).subscribe(rateCard => {
            this.createEditRateCard = rateCard;
            this.syncCitiesForCurrentRate();
            this.updateFormValues();
        });
    }

    updateFormValues() {
        if (!this.createEditRateCardForm) {
            return;
        }
        this.createEditRateCardForm.patchValue({
            vehicleTypeDropdown: this.vehicleTypes.find(type => type.id === this.createEditRateCard.vehicleTypeId),
            vehicleConfigDropdown: this.vehicleConfigs.find(config => config.configId === this.createEditRateCard.vehicleConfigId),
            vehicleCapacityDropdown: this.vehicleCapacities.find(capacity => capacity.capacityId === this.createEditRateCard.vehicleCapacityId),
            sourceCityInput: this.findCity(this.createEditRateCard.sourceCityId, this.createEditRateCard.sourceCity),
            sourceCountryInput: this.resolveCountryId(this.createEditRateCard.sourceCountryId, this.createEditRateCard.sourceCityId, this.createEditRateCard.sourceCountry),
            destinationCityInput: this.findCity(this.createEditRateCard.destinationCityId, this.createEditRateCard.destinationCity),
            destinationCountryInput: this.resolveCountryId(this.createEditRateCard.destinationCountryId, this.createEditRateCard.destinationCityId, this.createEditRateCard.destinationCountry),
            distanceInput: this.createEditRateCard.distance,
            amountInput: this.createEditRateCard.amount,
            goodsInsuranceInput: this.createEditRateCard.goodsInsurance,
            portPermitInput: this.createEditRateCard.portPermit,
            waitingThresholdInput: this.createEditRateCard.waitingThreshold,
            waitingChargeInput: this.createEditRateCard.waitingCharge,
        });
    }

    updateRateCardObject() {
        const sourceCity = this.createEditRateCardForm.get('sourceCityInput')?.value as City | undefined;
        const destinationCity = this.createEditRateCardForm.get('destinationCityInput')?.value as City | undefined;
        const sourceCountryId = sourceCity?.country?.id ?? this.createEditRateCardForm.get('sourceCountryInput')?.value;
        const destinationCountryId = destinationCity?.country?.id ?? this.createEditRateCardForm.get('destinationCountryInput')?.value;
        const sourceCountry = this.findCountry(sourceCountryId);
        const destinationCountry = this.findCountry(destinationCountryId);
        this.createEditRateCard = {
            ...this.createEditRateCard, // Keep existing properties
            vehicleTypeId: this.createEditRateCardForm.get('vehicleTypeDropdown')?.value.id,
            vehicleConfigId: this.createEditRateCardForm.get('vehicleConfigDropdown')?.value.configId,
            vehicleCapacityId: this.createEditRateCardForm.get('vehicleCapacityDropdown')?.value.capacityId,
            sourceCity: sourceCity?.cityName ?? '',
            sourceCountry: sourceCountry?.name,
            sourceCityId: sourceCity?.id,
            sourceCountryId,
            destinationCity: destinationCity?.cityName ?? '',
            destinationCountry: destinationCountry?.name,
            destinationCityId: destinationCity?.id,
            destinationCountryId,
            distance: this.createEditRateCardForm.get('distanceInput')?.value,
            amount: this.createEditRateCardForm.get('amountInput')?.value,
            goodsInsurance: this.createEditRateCardForm.get('goodsInsuranceInput')?.value ?? false,
            portPermit: this.createEditRateCardForm.get('portPermitInput')?.value ?? false,
            waitingThreshold: this.createEditRateCardForm.get('waitingThresholdInput')?.value,
            waitingCharge: this.createEditRateCardForm.get('waitingChargeInput')?.value,
            contractRevisionId: this.contractRevisionId!,
            buyingCompanyId: this.contract.secondParty.id,
            sellingCompanyId: this.contract.firstParty.id
        };
    }

    createUpdateRateCard() {
        this.rateCardCreationInProgress = true;
        this.updateRateCardObject();
        const rateCardData: DeliveryRateCard = this.createEditRateCard;
        console.log(rateCardData);

        // Create new rate card
        this.contractsService.createDeliveryRateCardsForRevision(this.contractId!, this.contractRevisionId!, [rateCardData])
            .subscribe(() => {
                this.rateCardCreationInProgress = false;
                // Handle success, e.g., show a message, navigate back
                //this.location.back();
            });
    }

    clearRateCardForm() {
        this.createEditRateCardForm.reset();
    }

    onVehicleTypeChange() {
        const selectedVehicleType: VehicleType = this.createEditRateCardForm.get('vehicleTypeDropdown')?.value;
        console.log(selectedVehicleType);
        if (selectedVehicleType) {
            this.filteredVehicleConfigs = this.vehicleConfigs.filter(config => config.typeId === selectedVehicleType.id);
            this.filteredVehicleCapacities = this.vehicleCapacities.filter(capacity => capacity.typeId === selectedVehicleType.id);
        } else {
            this.filteredVehicleConfigs = [];
            this.filteredVehicleCapacities = [];
        }
    }

    onSourceCountryChange(resetCity = true) {
        const countryId = this.selectedCountryId('sourceCountryInput', this.createEditRateCard.sourceCountryId);
        if (resetCity) {
            this.createEditRateCardForm.patchValue({ sourceCityInput: null }, { emitEvent: false });
            this.createEditRateCard.sourceCityId = undefined;
            this.createEditRateCard.sourceCity = '';
        }
        this.createEditRateCard.sourceCountryId = countryId;
        this.createEditRateCard.sourceCountry = this.findCountry(countryId)?.name;
        this.loadSourceCities(countryId);
    }

    onDestinationCountryChange(resetCity = true) {
        const countryId = this.selectedCountryId('destinationCountryInput', this.createEditRateCard.destinationCountryId);
        if (resetCity) {
            this.createEditRateCardForm.patchValue({ destinationCityInput: null }, { emitEvent: false });
            this.createEditRateCard.destinationCityId = undefined;
            this.createEditRateCard.destinationCity = '';
        }
        this.createEditRateCard.destinationCountryId = countryId;
        this.createEditRateCard.destinationCountry = this.findCountry(countryId)?.name;
        this.loadDestinationCities(countryId);
    }

    private findCity(cityId?: number, cityName?: string): City | undefined {
        const normalizedCityName = cityName?.trim().toLowerCase();
        return [...this.sourceCities, ...this.destinationCities].find(city => city.id === cityId)
            ?? [...this.sourceCities, ...this.destinationCities].find(city => city.cityName?.trim().toLowerCase() === normalizedCityName);
    }

    private resolveCountryId(countryId?: number, cityId?: number, countryName?: string): number | undefined {
        const normalizedCountry = countryName?.trim().toLowerCase();
        return countryId
            ?? this.findCity(cityId)?.country?.id
            ?? this.countries.find(country =>
                country.name?.trim().toLowerCase() === normalizedCountry
                || country.countryCode?.trim().toLowerCase() === normalizedCountry
            )?.id;
    }

    private findCountry(countryId?: number): Country | undefined {
        return this.countries.find(country => country.id === countryId);
    }

    private syncCitiesForCurrentRate() {
        this.loadSourceCities(this.resolveCountryId(this.createEditRateCard.sourceCountryId, this.createEditRateCard.sourceCityId, this.createEditRateCard.sourceCountry));
        this.loadDestinationCities(this.resolveCountryId(this.createEditRateCard.destinationCountryId, this.createEditRateCard.destinationCityId, this.createEditRateCard.destinationCountry));
    }

    private loadSourceCities(countryId?: number) {
        if (!countryId) {
            this.sourceCities = [];
            this.updateFormValues();
            return;
        }
        this.baseMasterDataService.getCities('en-US', countryId).subscribe(cities => {
            this.sourceCities = cities ?? [];
            this.updateFormValues();
        });
    }

    private loadDestinationCities(countryId?: number) {
        if (!countryId) {
            this.destinationCities = [];
            this.updateFormValues();
            return;
        }
        this.baseMasterDataService.getCities('en-US', countryId).subscribe(cities => {
            this.destinationCities = cities ?? [];
            this.updateFormValues();
        });
    }

    private selectedCountryId(controlName: string, fallback?: number): number | undefined {
        const value = this.createEditRateCardForm.get(controlName)?.value;
        const numericValue = Number(value);
        return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
    }

    private defaultRateCountries(): Country[] {
        return [
            { name: 'Saudi Arabia', countryCode: 'SA' },
            { name: 'United Arab Emirates', countryCode: 'AE' },
            { name: 'Bahrain', countryCode: 'BH' },
            { name: 'Qatar', countryCode: 'QR' },
            { name: 'Kuwait', countryCode: 'KW' },
            { name: 'Oman', countryCode: 'OM' }
        ] as Country[];
    }
}
